import { Badge, Button, Icons } from "../../../shared/ui";
import { AdminListRow } from "../AdminListRow";
import type { RoleItem } from "./accessApi";
import { groupPermissions, permissionLabel, roleLabel } from "./accessFormat";
import { useAccessT } from "./useAccessT";

/** Read-only roles master list. */
export function RoleList({
  roles,
  selectedId,
  hrefFor,
  onOpen,
}: {
  roles: readonly RoleItem[];
  selectedId: string | null;
  hrefFor: (roleId: string) => string;
  onOpen: (roleId: string) => void;
}) {
  const t = useAccessT();
  return (
    <ul
      aria-label={t("admin.access.list.roles")}
      className="divide-y divide-line-subtle overflow-hidden rounded-xl bg-raised shadow-card"
    >
      {roles.map((role) => (
        <li key={role.role_id}>
          <AdminListRow
            href={hrefFor(role.role_id)}
            selected={role.role_id === selectedId}
            onSelect={() => {
              onOpen(role.role_id);
            }}
          >
            <span className="flex min-w-0 items-center justify-between gap-2">
              <span className="truncate text-callout font-semibold text-fg">
                {roleLabel(t, role.role_id, role.display_name)}
              </span>
              <span className="flex shrink-0 items-center gap-1">
                {!role.active && (
                  <Badge tone="neutral">
                    {t("admin.access.role.inactive")}
                  </Badge>
                )}
                <Icons.ChevronRight size={16} className="text-fg-subtle" />
              </span>
            </span>
            <span className="text-caption text-fg-muted">
              {`${t("admin.access.role.operators")}: ${String(role.operator_count)}`}
            </span>
          </AdminListRow>
        </li>
      ))}
    </ul>
  );
}

/** One role with its permissions grouped by area; nothing to edit. */
export function RolePage({
  role,
  onBack,
}: {
  role: RoleItem | undefined;
  onBack?: () => void;
}) {
  const t = useAccessT();
  const back = onBack && (
    <Button variant="ghost" size="sm" onClick={onBack}>
      {t("admin.access.back")}
    </Button>
  );
  if (role === undefined) {
    return (
      <div className="flex flex-col gap-3">
        {back}
        <p className="text-body text-fg-muted">{t("admin.access.notFound")}</p>
      </div>
    );
  }
  const groups = groupPermissions(role.permissions);
  return (
    <article className="flex min-w-0 flex-col gap-3">
      {back}
      <header className="flex flex-col gap-1">
        <h2 className="text-title text-fg">
          {roleLabel(t, role.role_id, role.display_name)}
        </h2>
        <span className="flex items-center gap-2 text-footnote text-fg-muted">
          <Badge tone={role.active ? "success" : "neutral"}>
            {role.active
              ? t("admin.access.role.active")
              : t("admin.access.role.inactive")}
          </Badge>
          {`${t("admin.access.role.operators")}: ${String(role.operator_count)}`}
        </span>
        <code className="font-mono text-caption text-fg-muted">
          {role.role_id}
        </code>
      </header>
      <h3 className="text-footnote font-semibold text-fg-muted">
        {t("admin.access.role.permissions")}
      </h3>
      {groups.length === 0 ? (
        <p className="text-body text-fg-muted">
          {t("admin.access.role.noPermissions")}
        </p>
      ) : (
        groups.map(([group, ids]) => (
          <section key={group} className="flex flex-col gap-1">
            <p className="font-mono text-caption text-fg-subtle">{group}</p>
            <ul className="divide-y divide-line-subtle rounded-xl bg-raised px-4 shadow-card">
              {ids.map((id) => (
                <li key={id} className="flex flex-col py-2">
                  <span className="text-body text-fg">
                    {permissionLabel(t, id)}
                  </span>
                  <code className="font-mono text-caption text-fg-muted">
                    {id}
                  </code>
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
    </article>
  );
}
